import * as RadixPopover from '@radix-ui/react-tooltip'
import { useAtom } from '@reatom/npm-react'
import * as DateF from 'date-fns'
import { useMemo } from 'react'
import styled from 'styled-components'
import { match } from 'ts-pattern'

import { DurationFormatter, Numeric, Skeleton } from '@blade/ui-kit'
import { CurrencyModel } from '@root/entities/currency'
import { CurrencyIcon } from '@root/entities/currency/ui'
import { reatomPairMaker } from '@root/entities/trade/atoms'
import { Maker } from '@root/entities/trade/model'
import { MAKER_SIZE_DESCRIPTION } from '@root/entities/trade/ui/constants'
import { MakerIcon } from '@root/entities/trade/ui/maker-icon'
import { Trend } from '@root/entities/trend'
import { WalletAddressTruncated } from '@root/entities/wallet/ui/common/wallet-address-truncated'
import { AmountFormatted, PnlUsdFormatted } from '@root/shared/core/format'
import { useOptionalAtom } from '@root/shared/lib'
import { useDateFnsLocale, useTranslation } from '@root/shared/localization'

import type { Network } from '@root/entities/network'
import type { PairModel } from '@root/entities/pair/atoms'
import type { PairAddress } from '@root/entities/pair/model'
import type { PairMakerRequestModel } from '@root/entities/trade/atoms'
import type { ChildrenProps, ClassNameProps } from '@root/shared/react'

type MakerPopupProps = {
  maker: Maker.MakerShort
  network: Network.Network
  pairAddress: PairAddress
  pair: PairModel | undefined
  open?: boolean
  onOpenChange?: (open: boolean) => void
} & ClassNameProps &
  ChildrenProps

export const MakerPopup: React.FC<MakerPopupProps> = ({
  pairAddress,
  children,
  network,
  maker,
  open,
  onOpenChange,
  pair,
}) => {
  const makerModel = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomPairMaker({
        network,
        walletAddress: maker.address,
        pairAddress: pairAddress,
      }),
    [maker.address, network, pairAddress],
  )
  return (
    <Root open={open} onOpenChange={onOpenChange}>
      <RadixPopover.Trigger asChild>{children}</RadixPopover.Trigger>
      <RadixPopover.Portal>
        <Content sideOffset={6}>
          <Arrow />
          <PopupContent
            makerModel={makerModel}
            maker={maker}
            pairAddress={pairAddress}
            network={network}
            pair={pair}
          />
        </Content>
      </RadixPopover.Portal>
    </Root>
  )
}
const PopupContent: React.FC<
  MakerPopupProps & {
    makerModel: PairMakerRequestModel
  }
> = ({ makerModel, network, maker, pair }) => {
  const { t } = useTranslation()
  const locale = useDateFnsLocale()
  const [makerState] = useAtom(makerModel)

  const boughtUsd = makerState.value?.buyVolumeInUsd ?? maker.buyVolumeInUsd
  const soldUsd = makerState.value?.sellVolumeInUsd ?? maker.sellVolumeInUsd
  const balanceCurrency =
    makerState.value?.balanceCurrency ?? maker.balanceCurrency
  const volumeUsd = makerState.value?.volumeInUsd ?? maker.volumeInUsd
  const pnl = makerState.value?.pnl ?? maker.pnl

  const makerSize = Maker.makerSize(boughtUsd)
  const makerSizeDescription = t(MAKER_SIZE_DESCRIPTION[makerSize])
  const pairPriceUsd = useOptionalAtom(pair?.price)?.priceUsd

  const age = useMemo(() => {
    const now = new Date()
    const duration = DateF.intervalToDuration({
      start: maker.firstTradeAt,
      end: now,
    })
    return DurationFormatter.formatDurationSingleUnit(duration, { locale })
  }, [locale, maker.firstTradeAt])

  const totalPnl = useMemo(() => {
    if (!pairPriceUsd) {
      return pnl
    }
    // TODO: If we have pair price, we need to make pnl realtime
    // but we need entry price in usd
    return pnl
  }, [pairPriceUsd, pnl])

  const winRate30D = useMemo(() => {
    return match(makerState)
      .with({ type: 'Success' }, (state) => {
        return (
          <ItemValue data-trend={Trend.of(state.value.winRate30D ?? 0)}>
            {state.value.winRate30D ? (
              <>
                <Numeric value={state.value.winRate30D} preset="percents" />
                <span>%</span>
              </>
            ) : (
              <span>-</span>
            )}
          </ItemValue>
        )
      })
      .otherwise(() => {
        return (
          <ItemValue>
            <Skeleton
              height={12}
              paddingBlock={1}
              width={30}
              variant="rectangle"
            />
          </ItemValue>
        )
      })
  }, [makerState])

  const pnl30D = useMemo(() => {
    return match(makerState)
      .with({ type: 'Success' }, ({ value }) => {
        return (
          <ItemValue data-trend={Trend.of(value.pnl30D ?? 0)}>
            {value.pnl30D ? (
              <>
                <Numeric value={value.pnl30D} preset="percents" />
                <span>%</span>
              </>
            ) : (
              <span>-</span>
            )}
          </ItemValue>
        )
      })
      .otherwise(() => {
        return (
          <ItemValue>
            <Skeleton
              height={12}
              paddingBlock={1}
              width={36}
              variant="rectangle"
            />
          </ItemValue>
        )
      })
  }, [makerState])

  return (
    <>
      <TopRows>
        <Row>
          <ItemLabel>{t('position.bought')}</ItemLabel>
          <ItemBorder></ItemBorder>
          <ItemValue data-trend="up">
            <Numeric
              value={boughtUsd}
              preset="currency-amount-short"
              currency="$"
            />
          </ItemValue>
        </Row>
        <Row>
          <ItemLabel>{t('position.sold')}</ItemLabel>
          <ItemBorder></ItemBorder>
          <ItemValue data-trend="down">
            <Numeric
              value={soldUsd}
              preset="currency-amount-short"
              currency="$"
            />
          </ItemValue>
        </Row>
        <Row>
          <ItemLabel>{t('common.pnl')}</ItemLabel>
          <ItemBorder></ItemBorder>
          <ItemValue data-trend={Trend.of(totalPnl)}>
            <PnlUsdFormatted pnl={totalPnl} />
          </ItemValue>
        </Row>
        <Row>
          <ItemLabel>{t('common.balance')}</ItemLabel>
          <ItemBorder></ItemBorder>
          <ItemValue>
            <AmountFormatted amount={balanceCurrency} roundingMode="trunc" />
          </ItemValue>
        </Row>
      </TopRows>
      <BottomContent>
        <MakerInfoContainer>
          <MakerIcon volume={volumeUsd} />
          <MakerAddress walletAddress={maker.address} withCopy copySize={12} />
        </MakerInfoContainer>
        <BottomRows>
          <Row>
            <ItemLabel>{t('common.age')}</ItemLabel>
            <ItemBorder></ItemBorder>
            <ItemValue>{age}</ItemValue>
          </Row>
          <Row>
            <ItemLabel>{t('maker.30d-win-rate')}</ItemLabel>
            <ItemBorder></ItemBorder>
            {winRate30D}
          </Row>
          <Row>
            <ItemLabel>{t('common.30d-pnl')}</ItemLabel>
            <ItemBorder></ItemBorder>
            {pnl30D}
          </Row>
        </BottomRows>
        <SizeContainer>
          <CurrencyIcon size={14} currency={CurrencyModel.nativeOf(network)} />
          <SizeText>{makerSizeDescription}</SizeText>
        </SizeContainer>
      </BottomContent>
    </>
  )
}

export const Root = styled(RadixPopover.Root)``

export const Content = styled(RadixPopover.Content)`
  background: var(--background-2);
  color: var(--text-10);
  border-radius: 6px;
  box-shadow: 0 2px 14px 0 var(--appearanceA-2);
  width: 180px;

  overflow: hidden;
  padding: 2px;
`

const Row = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 2px;
`

const TopRows = styled.div`
  display: grid;
  gap: 4px;
  padding-block: 12px 10px;
  padding-inline: 12px;
`

const BottomRows = styled.div`
  display: grid;
  gap: 4px;
  margin-bottom: 8px;
`

const ItemLabel = styled.div`
  color: var(--text-9);

  font-size: 10px;
  font-weight: 500;
  line-height: 12px; /* 120% */
`

const ItemBorder = styled.div`
  height: 10px;
  border-bottom: 1px dashed var(--background-4);
`

const ItemValue = styled.div`
  color: var(--text-11);

  font-size: 10px;
  font-weight: 600;
  line-height: 12px;

  &[data-trend='up'] {
    color: var(--up-9);
  }

  &[data-trend='neutral'] {
    color: var(--text-11);
  }

  &[data-trend='down'] {
    color: var(--down-9);
  }
`

const BottomContent = styled.div`
  border-radius: 4px;
  background: var(--background-1);
  padding-block: 10px;
  padding-inline: 10px;
`

const Arrow = styled(RadixPopover.Arrow)`
  fill: var(--background-2);
`

const MakerInfoContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 4px;
  align-items: center;
  justify-content: center;
  margin-bottom: 2px;
`
const MakerAddress = styled(WalletAddressTruncated)`
  gap: 4px;
  color: var(--text-12);
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
`

const SizeContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 4px;
  justify-content: center;
  align-items: center;
`

const SizeText = styled.div`
  color: var(--text-8);
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
`
